/**
 * This details-table renders the following configs on the Refinance Calculator:
 * - client/components/refinance-calculator/rc-results-panel/details-table/rc-details-table-accordion-config.ts - 3 items
 */

// Dependencies

import React, { useState } from 'react';

import classnames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';
import baseStylesVars from '@nerdwallet/base-styles/variables';

// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import { fill, get, map, isNaN } from 'lodash';
import numeral from 'numeral';
import { connect } from 'react-redux';

import { Impression } from '@nerdwallet/analytics/react/impression';
import { Text, Box } from '@nerdwallet/currency';
import { ControlledAccordion } from '@nerdwallet/react-accordion';

import {
  ENTITY_NAME,
  getEventDetails,
  getEventEntityName,
  getEventType,
  getRefinanceCalculatorSummary,
} from '../../../../lib/analytics-constants';
import {
  inputsSelector,
  outputsSelector,
} from '../../../../lib/selectors/refinance-calculator-selectors';
import { RefinanceInput } from '../../../../lib/utils';

import {
  RCDetailsTableAccordion,
  SIGN,
  VARIABLE,
} from './rc-details-table-accordion-config';

// @ts-ignore - ignore error
import styles from './details-table.module.less';

interface DetailsTableProps extends Pick<RefinanceInput, 'inputs' | 'outputs'> {
  /**
   * Styling class name
   */
  className?: string;
  /**
   * Details to display.
   */
  viewConfig: RCDetailsTableAccordion[];
}

export const formatMoney = (value: any): number | string =>
  isNaN(value) ? 0 : numeral(value).format('$0a');

/**
 * Component that renders the results summary section (Accordion).
 * @param props DetailsTableProps (obtained from config file + reducer data + className overrides)
 */
const DetailsTable: React.FC<DetailsTableProps> = ({
  className: classNameProp,
  inputs,
  outputs,
  viewConfig,
}) => {
  const [expandedMap, setExpandedMap] = useState(
    fill(Array(viewConfig.length), false)
  );

  const toggleItem = (index) => {
    const newExpandedMap = [...expandedMap];
    newExpandedMap[index] = !newExpandedMap[index];
    setExpandedMap(newExpandedMap);
  };

  const getVariableRows = (rows) =>
    map(rows, (row, i) => {
      const resolvedValue = row.value
        ? formatMoney(get(outputs, row.value))
        : row.text;
      return (
        <Box key={i} className={styles.equationRow}>
          {resolvedValue || '\u00a0'}
        </Box>
      );
    });

  const renderEquation = ({ className, config }) => {
    const equationParts = map(config, ({ type, rows, value }, index) => {
      switch (type) {
        case SIGN:
          return (
            <Box key={index} className={styles.equationSign}>
              {value}
            </Box>
          );
        case VARIABLE:
          return (
            <Box key={index} className={styles.equationVariable}>
              {getVariableRows(rows)}
            </Box>
          );
        default:
          return null;
      }
    });

    return (
      <Box className={classnames(styles.rcDetailsEquation, styles[className])}>
        {equationParts}
      </Box>
    );
  };

  const howLongStay = get(inputs, 'howLongStay');
  const accordionProps = {
    analytics: {
      enabled: true,
      payload: {
        elementName: ENTITY_NAME.MORTGAGES_REFI_CALCULATOR_SUMMARY_DETAILS,
        sectionPosition: 1,
      },
    },
    items: map(
      viewConfig,
      (
        {
          header,
          trackingFieldName,
          headerValue,
          help,
          className,
          contentConfig,
        },
        index
      ) => ({
        id: trackingFieldName,
        expanded: expandedMap[index],
        onSelect: () => toggleItem(index),
        title: (
          <Box display="flex" flexDirection="row" alignItems="center">
            <Box className={classes('flex-1')}>
              <Text size="small" bold>
                {header}
              </Text>
            </Box>
            <Box
              className={classnames(
                classes('margin-right-2', 'margin-left-2', 'text-align-right')
              )}
            >
              <Text size="small" bold>
                {formatMoney(get(outputs, headerValue))}
              </Text>
            </Box>
          </Box>
        ),
        children: (
          <Box
            // This spacing counteracts the padding on the AccordionItem
            style={{ marginTop: `calc(${baseStylesVars.spacing['3']} * -1)` }}
          >
            <Box paddingBottom={2}>
              <Text size="small">{help}</Text>
            </Box>
            {renderEquation({
              className,
              config: contentConfig,
            })}
          </Box>
        ),
      })
    ),
  };

  const summaryEvent = getRefinanceCalculatorSummary();
  const summaryEventDetails = getEventDetails(summaryEvent);

  return (
    <Impression
      id="rc-details-table-summary"
      className={`${styles.rcDetailsTable} ${classNameProp}`}
      eventType={getEventType(summaryEvent)}
      entityName={getEventEntityName(summaryEvent)}
      payload={summaryEventDetails}
    >
      <Text size="large">{`Through year ${howLongStay}`}</Text>
      <Box marginTop={2} paddingBottom={3} borderBottom={2}>
        <Text size="small">
          Tax deductions on interest paid have not been factored in
        </Text>
      </Box>
      <ControlledAccordion {...accordionProps} />
    </Impression>
  );
};

DetailsTable.displayName = 'DetailsTable';

DetailsTable.defaultProps = {
  className: '',
};

const mapStateToProps = (state) => ({
  outputs: outputsSelector(state),
  inputs: inputsSelector(state),
});

export default connect(mapStateToProps)(DetailsTable);
